import {
    Autocomplete,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    MenuItem,
    Stack,
    TextField,
} from '@mui/material';
import * as React from 'react';
import {Formik} from '../../application/components/Formik';
import {FormikHelpers} from 'formik/dist/types';
import {FormikValues} from 'formik';
import * as Yup from 'yup';
import AutocompleteAsync from './AutocompleteAsync';
import {DocumentNode} from 'graphql/language';
import {DatePicker} from '@mui/x-date-pickers';
import {Dayjs} from 'dayjs';

export type EditorFieldType =
    | 'NUMBER'
    | 'DATEPICKER'
    | 'TEXT'
    | 'TEXTAREA'
    | 'SELECT'
    | 'HIDDEN'
    | 'CHECKBOX'
    | 'AUTOCOMPLETE'
    | 'AUTOCOMPLETE_ASYNC';

export type SelectOption = {
    key: string;
    displayElement: React.JSX.Element;
};

function isEditorFieldKind(object: object, type: string) {
    return !!object && 'type' in object && typeof object.type === 'string' && object.type === type;
}

function isDatepickerEditorField(object: object): object is DatePickerEditorField {
    return isEditorFieldKind(object, 'DATEPICKER');
}

function isCheckboxEditorField(object: object): object is BooleanEditorField {
    return isEditorFieldKind(object, 'CHECKBOX');
}

function isSelectEditorField(object: object): object is SelectEditorField {
    return isEditorFieldKind(object, 'SELECT');
}

function isAutocompleteEditorField(object: object): object is AutocompleteEditorField {
    return isEditorFieldKind(object, 'AUTOCOMPLETE');
}

function isAutocompleteAsyncEditorField(object: object): object is AutocompleteAsyncEditorField {
    return isEditorFieldKind(object, 'AUTOCOMPLETE_ASYNC');
}

export type RegularEditorField = {
    key: string;
    label: string;
    type: Omit<EditorFieldType, 'SELECT | AUTOCOMPLETE' | 'AUTOCOMPLETE_ASYNC' | 'CHECKBOX' | 'DATEPICKER'>;
    additionalProps?: any;
    editable: boolean | ((object: any) => boolean);
};

export type DatePickerEditorField = Omit<RegularEditorField, 'type'> & {
    type: 'DATEPICKER';
    restrictToDates?: Dayjs[];
};

export type BooleanEditorField = Omit<RegularEditorField, 'type'> & {
    type: 'CHECKBOX';
    icon?: React.ReactNode;
    checkedIcon?: React.ReactNode;
};

export type SelectEditorField = Omit<RegularEditorField, 'type'> & {
    type: 'SELECT';
    selectOptions: SelectOption[];
    selectOptionsSupplier?: (values: any) => SelectOption[];
};

export type AutocompleteEditorField = Omit<RegularEditorField, 'type'> & {
    type: 'AUTOCOMPLETE';
    options: any[];
    getOptionLabel: (option: any) => string;
    isOptionEqualToValue: (option: any, value: any) => boolean;
};

export type AutocompleteAsyncEditorField = Omit<AutocompleteEditorField, 'type' | 'options'> & {
    type: 'AUTOCOMPLETE_ASYNC';
    query: DocumentNode;
    queryToOptionsMapper: (data: any) => any;
};

export type EditorField =
    | RegularEditorField
    | SelectEditorField
    | AutocompleteEditorField
    | AutocompleteAsyncEditorField
    | DatePickerEditorField
    | BooleanEditorField;

export type FormProps<T> = {
    fields: EditorField[];
    fieldsSupplier?: (values: T) => EditorField[];
    generatedValuesSupplier?: (values: T) => Partial<T>;
    initialValues: T;
    validationSchema: Yup.ObjectSchema<any>;
    previewOfChange?(t: T): React.JSX.Element;
    autoSubmit?: boolean;
    showControlButtons?: boolean;
    onSave: (value: T) => void | Promise<unknown>;
    onCancel: () => void;
    onChange?(value: T): void;
    presentation?: 'default' | 'dialog';
    submitLabel?: string;
    submitColor?: 'primary' | 'secondary' | 'success' | 'error';
};

export default function Form<T>({
    fields,
    fieldsSupplier,
    generatedValuesSupplier,
    initialValues,
    validationSchema,
    previewOfChange,
    autoSubmit,
    showControlButtons,
    onSave,
    onCancel,
    onChange,
    presentation = 'default',
    submitLabel,
    submitColor,
}: FormProps<T>) {
    if (showControlButtons === undefined) {
        showControlButtons = true;
    }

    function getFieldUniqueProps(editorField: EditorField) {
        switch (editorField.type) {
            case 'TEXT':
                return {
                    disabled: !editorField.editable,
                    type: 'text',
                };
            case 'TEXTAREA':
                return {
                    disabled: !editorField.editable,
                    type: 'text',
                    multiline: true,
                };
            case 'DATEPICKER':
                return {
                    disabled: !editorField.editable,
                    type: 'date',
                };
            case 'NUMBER':
                return {
                    disabled: !editorField.editable,
                    type: 'number',
                };
            case 'SELECT':
                return {
                    disabled: !editorField.editable,
                    select: true,
                };
            default:
                throw new Error('not known field type ' + editorField.type);
        }
    }

    function createTextField(editorField: EditorField, formik: any) {
        const selectOptions = isSelectEditorField(editorField)
            ? editorField.selectOptionsSupplier?.(formik.values) ?? editorField.selectOptions
            : [];

        return (
            <TextField
                {...getFieldUniqueProps(editorField)}
                {...(editorField.additionalProps || {})}
                fullWidth
                variant={presentation === 'dialog' ? 'filled' : 'standard'}
                size={presentation === 'dialog' ? 'small' : undefined}
                id={editorField.key}
                name={editorField.key}
                key={editorField.key}
                label={editorField.label}
                value={formik.values[editorField.key] ?? ''}
                slotProps={
                    isSelectEditorField(editorField)
                        ? {
                              inputLabel: {
                                  htmlFor: undefined,
                              },
                          }
                        : undefined
                }
                onChange={e => {
                    formik.handleChange(e);
                    if (autoSubmit) {
                        formik.submitForm();
                    }
                }}
                onBlur={formik.handleBlur}
                error={formik.touched[editorField.key] && Boolean(formik.errors[editorField.key])}
                helperText={formik.touched[editorField.key] && formik.errors[editorField.key]}
            >
                {isSelectEditorField(editorField) &&
                    selectOptions.map(option => (
                        <MenuItem key={option.key} value={option.key}>
                            {option.displayElement}
                        </MenuItem>
                    ))}
            </TextField>
        );
    }

    function createDatePicker(editorField: DatePickerEditorField, formik: any) {
        return (
            <DatePicker
                {...getFieldUniqueProps(editorField)}
                {...(editorField.additionalProps || {})}
                fullWidth
                variant={presentation === 'dialog' ? 'filled' : 'standard'}
                slotProps={
                    presentation === 'dialog'
                        ? {textField: {variant: 'filled', size: 'small', fullWidth: true}}
                        : undefined
                }
                id={editorField.key}
                name={editorField.key}
                key={editorField.key}
                label={editorField.label}
                value={formik.values[editorField.key]}
                format={'YYYY-MM-DD'}
                onChange={(newValue: Dayjs) => {
                    formik.setFieldValue(editorField.key, newValue, true);
                    if (autoSubmit) {
                        formik.submitForm();
                    }
                }}
                onBlur={formik.handleBlur}
                error={formik.touched[editorField.key] && Boolean(formik.errors[editorField.key])}
                helperText={formik.touched[editorField.key] && formik.errors[editorField.key]}
                shouldDisableDate={day => {
                    const restrictedDays = editorField.restrictToDates || [];
                    return (
                        restrictedDays.length > 0 &&
                        restrictedDays.filter(date => day.startOf('day').isSame(date.startOf('day'))).length === 0
                    );
                }}
            />
        );
    }

    function createAutocomplete(editorField: AutocompleteEditorField, formik: any) {
        return (
            <Autocomplete
                fullWidth
                disabled={!editorField.editable}
                id={editorField.key}
                key={editorField.key}
                value={formik.values[editorField.key]}
                onBlur={formik.handleBlur}
                onChange={(e, newValue) => {
                    formik.setFieldValue(editorField.key, newValue, true);
                    if (autoSubmit) {
                        formik.submitForm();
                    }
                }}
                getOptionLabel={option => editorField.getOptionLabel(option)}
                isOptionEqualToValue={editorField.isOptionEqualToValue}
                options={editorField.options}
                renderInput={params => (
                    <TextField
                        {...params}
                        variant={presentation === 'dialog' ? 'filled' : 'standard'}
                        size={presentation === 'dialog' ? 'small' : undefined}
                        label={editorField.label}
                        error={formik.touched[editorField.key] && Boolean(formik.errors[editorField.key])}
                        helperText={formik.touched[editorField.key] && formik.errors[editorField.key]}
                    />
                )}
            />
        );
    }

    function createCheckbox(editorField: BooleanEditorField, formik: any) {
        return (
            <FormControl key={editorField.key}>
                <FormHelperText error={formik.touched[editorField.key] && Boolean(formik.errors[editorField.key])}>
                    {formik.touched[editorField.key] && formik.errors[editorField.key]}
                </FormHelperText>
                <FormControlLabel
                    label={editorField.label}
                    id={editorField.key}
                    name={editorField.key}
                    key={editorField.key}
                    onChange={e => {
                        formik.handleChange(e);
                        if (autoSubmit) {
                            formik.submitForm();
                        }
                    }}
                    onBlur={formik.handleBlur}
                    {...(editorField.additionalProps || {})}
                    control={
                        <Checkbox
                            disabled={!editorField.editable}
                            checked={formik.values[editorField.key]}
                            size={'small'}
                            icon={editorField.icon}
                            checkedIcon={editorField.checkedIcon}
                        />
                    }
                />
            </FormControl>
        );
    }

    const Form = (formik: any) => {
        const generatedValuesRef = React.useRef<Record<string, unknown>>({});

        React.useEffect(() => {
            onChange?.(formik.values as T);
        }, [formik.values]);

        React.useEffect(() => {
            const generatedValues = generatedValuesSupplier?.(formik.values as T);
            if (!generatedValues) {
                return;
            }
            Object.entries(generatedValues).forEach(([key, value]) => {
                const previousGeneratedValue = generatedValuesRef.current[key];
                const mayGenerate =
                    !(key in generatedValuesRef.current) || formik.values[key] === previousGeneratedValue;
                if (!mayGenerate) {
                    return;
                }
                generatedValuesRef.current[key] = value;
                if (formik.values[key] !== value) {
                    formik.setFieldValue(key, value, false);
                }
            });
        }, [formik]);

        return (
            <form onSubmit={formik.handleSubmit} style={{width: '100%'}}>
                <Stack
                    direction="column"
                    spacing={presentation === 'dialog' ? 2 : 4}
                    alignItems={presentation === 'dialog' ? 'stretch' : 'center'}
                    sx={{width: '100%'}}
                >
                    {(fieldsSupplier?.(formik.values as T) ?? fields)
                        .filter(field => field.type !== 'HIDDEN')
                        .map(editorField => {
                            if (isDatepickerEditorField(editorField)) {
                                return createDatePicker(editorField, formik);
                            } else if (isCheckboxEditorField(editorField)) {
                                return createCheckbox(editorField, formik);
                            } else if (isAutocompleteEditorField(editorField)) {
                                return createAutocomplete(editorField, formik);
                            } else if (isAutocompleteAsyncEditorField(editorField)) {
                                return AutocompleteAsync({
                                    formik: formik,
                                    editorField: editorField,
                                });
                            } else {
                                return createTextField(editorField, formik);
                            }
                        })}
                    {previewOfChange?.(formik.values)}
                    {showControlButtons && presentation === 'dialog' && (
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{width: '100%', pt: 1}}>
                            <Button
                                sx={{flex: {xs: 1, sm: '0 0 auto'}}}
                                onClick={e => {
                                    e.stopPropagation();
                                    onCancel();
                                }}
                            >
                                Anuluj
                            </Button>
                            <Button
                                variant="contained"
                                color={submitColor}
                                type="submit"
                                disabled={formik.isSubmitting}
                                sx={theme => {
                                    const semanticPalette =
                                        submitColor === 'success' || submitColor === 'error'
                                            ? theme.palette[submitColor]
                                            : null;
                                    const backgroundColor = semanticPalette
                                        ? theme.palette.mode === 'light'
                                            ? semanticPalette.dark
                                            : semanticPalette.light
                                        : undefined;

                                    return {
                                        flex: {xs: 1, sm: '0 0 auto'},
                                        ...(backgroundColor && {
                                            bgcolor: backgroundColor,
                                            color: theme.palette.getContrastText(backgroundColor),
                                            '&:hover': {bgcolor: backgroundColor},
                                        }),
                                    };
                                }}
                                onClick={e => e.stopPropagation()}
                            >
                                {submitLabel ?? 'Zapisz'}
                            </Button>
                        </Stack>
                    )}
                    {showControlButtons && presentation === 'default' && (
                        <Stack direction={'row'} spacing={4} alignItems={'center'} justifyContent={'space-evenly'}>
                            <Button
                                variant="text"
                                color={submitColor ?? 'secondary'}
                                type="submit"
                                disabled={formik.isSubmitting}
                                sx={{flexGrow: 1}}
                                onClick={e => e.stopPropagation()}
                            >
                                {submitLabel ?? 'Potwierdź'}
                            </Button>
                            <Button
                                variant="text"
                                sx={{flexGrow: 1}}
                                onClick={e => {
                                    e.stopPropagation();
                                    onCancel();
                                }}
                            >
                                Anuluj
                            </Button>
                        </Stack>
                    )}
                </Stack>
            </form>
        );
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values: T, {setSubmitting}: FormikHelpers<FormikValues>) => {
                try {
                    await onSave(values);
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {Form}
        </Formik>
    );
}
